import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface MetricRow {
  readonly analyticsId: string;
  readonly metricDate: string;
  readonly metricCode: string;
  readonly scopeType: string;
  readonly scopeRef: string | null;
  readonly metricValue: string;
  readonly targetValue: string | null;
  readonly isBreach: boolean;
}

const METRICS = ['', 'RECOVERY_SUCCESS_RATE', 'BACKUP_SUCCESS_RATE', 'AVAILABILITY_PCT', 'RTO_ACHIEVEMENT', 'RPO_ACHIEVEMENT', 'DR_READINESS_SCORE', 'RECOVERY_TREND', 'TEST_RESULT'];
const SCOPES = ['', 'GLOBAL', 'SERVICE', 'SYSTEM'];

/**
 * M31 — Recovery Analytics Dashboard (§23 / §31). Time-series of resilience KPIs
 * per metric code and scope with the captured value, target and breach flag
 * (LAW 6: targets are tenant params, never hard-coded). Filterable by metric
 * code and scope.
 */
export function RecoveryAnalyticsPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<MetricRow[]>([]);
  const [metricCode, setMetricCode] = useState('');
  const [scopeType, setScopeType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (metricCode) params.set('metricCode', metricCode);
    if (scopeType) params.set('scopeType', scopeType);
    const qs = params.toString();
    apiRequest<{ data: MetricRow[] }>(`/v1/bcm/analytics${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, metricCode, scopeType]);

  const breachCount = rows.filter((r) => r.isBreach).length;

  return (
    <main aria-label="recovery-analytics">
      <h1>{t('m31.analytics.title')}</h1>
      <div className="filters">
        <label>
          {t('m31.analytics.filterMetric')}
          <select aria-label="filter-metric" value={metricCode} onChange={(e) => setMetricCode(e.target.value)}>
            {METRICS.map((m) => <option key={m || 'all'} value={m}>{m || t('m31.analytics.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m31.analytics.filterScope')}
          <select aria-label="filter-scope" value={scopeType} onChange={(e) => setScopeType(e.target.value)}>
            {SCOPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m31.analytics.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m31.analytics.loading')}</p> : (
        <>
          <p aria-label="metric-count">{t('m31.analytics.count', { count: rows.length })}</p>
          <p aria-label="breach-count">{t('m31.analytics.breachCount', { count: breachCount })}</p>
          <table aria-label="analytics-table">
            <thead><tr><th>{t('m31.analytics.thDate')}</th><th>{t('m31.analytics.thMetric')}</th><th>{t('m31.analytics.thScope')}</th><th>{t('m31.analytics.thValue')}</th><th>{t('m31.analytics.thTarget')}</th><th>{t('m31.analytics.thBreach')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.analyticsId} data-breach={r.isBreach ? 'yes' : 'no'}>
                  <td>{day(r.metricDate)}</td><td>{r.metricCode}</td><td>{r.scopeType}</td>
                  <td>{r.metricValue}</td><td>{r.targetValue ?? '—'}</td>
                  <td>{r.isBreach ? 'BREACH' : 'OK'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
